import { useApi, useWorkspace } from '~/contexts';
import { useField, useIsMounted } from '~/hooks';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { colors, weights } from '~/styles';
import { AutoComplete, BillableIcon, FieldControl, Tooltip, Dropdown } from '~/components/index.js';
import styled from 'styled-components';
import _ from 'lodash';

const Group = styled(Dropdown.Text)`
  font-weight: ${weights.bold};
  font-size: 1rem;
  color: ${colors.grey100};
  padding-left: 1rem;
`;

const Display = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;

  > div {
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

function ProjectRoleSelect({ value, project, onSearch, memberId, onChange, isNew, name, ...props }) {
  const api = useApi();
  const isMounted = useIsMounted();
  const { workspace } = useWorkspace();
  const [defaultOptions, setDefaultOptions] = useState([]);
  const [assignRole, setAssignRole] = useState(false);
  const assignedRolesRef = useRef([]);

  const handleSearch = useCallback(
    async (q) => {
      if (onSearch) {
        return onSearch(q);
      }

      const { data } = await api.www.workspaces(workspace.id).allocations().getProjectRoles(project.id, {
        q,
        memberId,
      });

      assignedRolesRef.current = data.filter((r) => r.assignedToMe);

      const groups = _.orderBy(
        data.reduce((a, v) => {
          const key = v.assignedToMe ? 'Assigned Roles' : 'Other Roles';
          a[key] = a[key] || { key, roles: [] };
          a[key].roles.push(v);
          return a;
        }, {}),
        'key',
      );

      // A lone group header carries no information, so the grouping only renders when both groups are populated.
      const options = [];
      for (const group of groups) {
        if (groups.length > 1) options.push({ type: 'group', key: group.key, disabled: true });
        for (const role of group.roles) {
          options.push({ type: 'role', role, disabled: false });
        }
      }

      if (assignedRolesRef.current.length === 1) {
        setAssignRole(true);
      }

      return options;
    },
    [workspace.id, api, project.id, onSearch, memberId],
  );

  const handleChange = (event) => {
    const selectedRole = event.target.value?.role;
    onChange?.({ target: { name: event.target.name, value: selectedRole } });
  };

  useEffect(() => {
    (async () => {
      const data = await handleSearch('');
      if (!isMounted.current) return;
      setDefaultOptions(data);
    })();
  }, [handleSearch, isMounted]);

  useEffect(() => {
    if (!assignRole || !isNew) return;
    setAssignRole(false);

    if (assignedRolesRef.current.length !== 1 || !_.isFunction(onChange)) return;

    onChange({ target: { name, value: assignedRolesRef.current[0] } });
  }, [assignRole, isNew, value, onChange, name]);

  return (
    <AutoComplete
      onSearch={handleSearch}
      value={value}
      displayText={value?.name ?? ''}
      defaultOptions={defaultOptions}
      onChange={handleChange}
      renderValue={(v) => v?.role?.name ?? ''}
      name={name}
      {...props}>
      {({ options }) => {
        let index = 0;
        const grouped = options.some((o) => o.type === 'group');
        return options.length > 0 ? (
          options.map((o) => {
            switch (o.type) {
              case 'group':
                return (
                  <Group key={o.key}>
                    <strong>{o.key}</strong>
                  </Group>
                );
              case 'role':
                return (
                  <AutoComplete.Option
                    key={o.role.id}
                    value={o}
                    index={index++}
                    tooltip={o.role.name}
                    style={{ padding: grouped ? '0.25rem 2rem' : '0.25rem 1rem' }}>
                    <Display>
                      <BillableIcon value={o.role.isActuallyBillable} />
                      <Tooltip message={o.role.name}>
                        <span>{o.role.name}</span>
                      </Tooltip>
                    </Display>
                  </AutoComplete.Option>
                );
              default:
                return null;
            }
          })
        ) : (
          <Dropdown.Text>None</Dropdown.Text>
        );
      }}
    </AutoComplete>
  );
}

function FieldProjectRoleSelect(props) {
  const [field, meta] = useField(props);
  const error = meta.touched && meta.error;

  return (
    <FieldControl error={error}>
      <ProjectRoleSelect {...field} {...props} />
    </FieldControl>
  );
}

export default ProjectRoleSelect;
export { FieldProjectRoleSelect };
